// Central planner-draft store.
//
// Contract:
//  * One draft object, persisted to localStorage under a single key.
//  * Every mutation runs through patchDraft/patchBody, which:
//     - stamps updated_at
//     - refreshes draft_expires_at to now + 30 days
//     - optionally recomputes downstream calorie/macro values
//     - optionally clears generation-result cache
//  * The store is reactive via useDraft().
//  * On page load the persisted draft is discarded if it has expired.

import { useEffect, useState } from 'react';
import {
  estimateCalories,
  deriveMacroTargets,
  resolveEffectiveBasis,
} from './plannerCalc';

const STORAGE_KEY = 'etff.plannerDraft.v1';
export const DRAFT_VERSION = 1;
export const DRAFT_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function newExpiry() {
  return new Date(Date.now() + DRAFT_TTL_MS).toISOString();
}

export const DEFAULT_DRAFT = Object.freeze({
  version: DRAFT_VERSION,
  step_reached: 1,          // progress indicator only, never proof of validity
  updated_at: null,
  draft_expires_at: null,   // set on first mutation

  // Screen 2
  days: null,
  meals_per_day: null,

  // Screen 3
  health_goal: null,

  // Screen 4
  body: {
    age: '',
    sex: null,                     // customer identity: male/female/other/prefer_not_to_say
    calculation_basis: null,       // male_equation / female_equation / manual (required only for other/prefer)
    height_unit: 'imperial',
    height_ft: '',
    height_in: '',
    height_cm: '',
    weight_unit: 'lb',
    weight_value: '',
    activity_level: null,
    normalized_height_cm: null,
    normalized_weight_kg: null,
  },

  // Screen 5
  calorie_source: null,
  calorie_target: null,

  // Screen 6
  macro_preset: null,
  protein_g: '',
  carbs_g: '',
  fat_g: '',

  // Screens 7–9
  allergies: [],
  allergies_other_description: '',
  restrictions: [],
  restrictions_other_description: '',
  preferences: [],

  // Reserved for Phase 2B generation result — cleared on any upstream change.
  generated_plan_ids: null,
  generation_timestamp: null,
  generation_meal_dataset_version: null,
  generation_planner_rules_version: null,
});

// -------------------- storage helpers --------------------------------
function isBrowser() {
  return typeof window !== 'undefined' && !!window.localStorage;
}

function isExpired(d) {
  if (!d?.draft_expires_at) return false;
  const t = Date.parse(d.draft_expires_at);
  return Number.isFinite(t) && t < Date.now();
}

function load() {
  if (!isBrowser()) return { ...DEFAULT_DRAFT };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_DRAFT };
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== DRAFT_VERSION) return { ...DEFAULT_DRAFT };
    if (isExpired(parsed)) {
      window.localStorage.removeItem(STORAGE_KEY);
      return { ...DEFAULT_DRAFT };
    }
    return {
      ...DEFAULT_DRAFT,
      ...parsed,
      body: { ...DEFAULT_DRAFT.body, ...(parsed.body || {}) },
    };
  } catch {
    return { ...DEFAULT_DRAFT };
  }
}

function persist(d) {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(d));
  } catch {
    /* quota errors are non-fatal — the wizard keeps working in memory */
  }
}

// -------------------- Dependency invalidation -----------------------
// Recompute derived downstream values from the current draft.
//   * calorie_target refreshes when calorie_source === 'calculated'
//   * preset macros refresh when macro_preset in {auto, higher_protein, lower_carb}
//   * custom macros are never overwritten
// Returns a new draft object.
export function recompute(draft) {
  const d = { ...draft, body: { ...draft.body } };

  // 1) Calorie target
  if (d.calorie_source === 'calculated') {
    const basis = resolveEffectiveBasis(d.body.sex, d.body.calculation_basis);
    if (basis && d.health_goal && d.body.activity_level) {
      const est = estimateCalories({
        basis,
        age: Number(d.body.age),
        height_cm: Number(d.body.normalized_height_cm),
        weight_kg: Number(d.body.normalized_weight_kg),
        activity_level: d.body.activity_level,
        health_goal: d.health_goal,
      });
      if (est?.calorie_target) d.calorie_target = est.calorie_target;
    }
  }

  // 2) Preset macros
  if (
    d.macro_preset &&
    d.macro_preset !== 'custom' &&
    Number.isFinite(Number(d.calorie_target))
  ) {
    const m = deriveMacroTargets({
      preset: d.macro_preset,
      calorie_target: d.calorie_target,
      weight_kg: d.body.normalized_weight_kg,
    });
    if (m) {
      d.protein_g = m.protein_g;
      d.carbs_g = m.carbs_g;
      d.fat_g = m.fat_g;
    }
  }

  return d;
}

function clearGeneration(d) {
  return {
    ...d,
    generated_plan_ids: null,
    generation_timestamp: null,
    generation_meal_dataset_version: null,
    generation_planner_rules_version: null,
  };
}

// -------------------- reactive store --------------------------------
let current = load();
const listeners = new Set();

function emit() {
  for (const l of listeners) l(current);
}

export function getDraft() {
  return current;
}

// General patch — merges + touches metadata. Options:
//   { recompute: true }        → run downstream recompute
//   { invalidateGeneration: true } → clear plan cache
export function patchDraft(update, opts = {}) {
  const merged =
    typeof update === 'function'
      ? { ...current, ...update(current) }
      : { ...current, ...update };
  let next = {
    ...merged,
    updated_at: new Date().toISOString(),
    draft_expires_at: newExpiry(),
  };
  if (opts.recompute) next = recompute(next);
  if (opts.invalidateGeneration) next = clearGeneration(next);
  current = next;
  persist(current);
  emit();
}

// Nested body patch. Convenience so screens don't spread body themselves.
export function patchBody(update, opts = {}) {
  const nextBody = { ...current.body, ...update };
  let next = {
    ...current,
    body: nextBody,
    updated_at: new Date().toISOString(),
    draft_expires_at: newExpiry(),
  };
  if (opts.recompute) next = recompute(next);
  if (opts.invalidateGeneration) next = clearGeneration(next);
  current = next;
  persist(current);
  emit();
}

export function bumpStepReached(step) {
  if (step > (current.step_reached || 1)) patchDraft({ step_reached: step });
}

export function resetDraft() {
  current = { ...DEFAULT_DRAFT };
  if (isBrowser()) window.localStorage.removeItem(STORAGE_KEY);
  // Start Over must not carry over a stale generation_request_id or
  // cached generation result. Import lazily to avoid a circular
  // dependency at module load.
  try {
    // eslint-disable-next-line global-require
    const gen = require('./mealGeneration');
    if (typeof gen.clearGenerationRequest === 'function') gen.clearGenerationRequest();
  } catch { /* ignore */ }
  emit();
}

// -------------------- React hook ------------------------------------
export function useDraft() {
  const [state, setState] = useState(current);
  useEffect(() => {
    const l = (d) => setState(d);
    listeners.add(l);
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) {
        current = load();
        emit();
      }
    };
    if (isBrowser()) window.addEventListener('storage', onStorage);
    return () => {
      listeners.delete(l);
      if (isBrowser()) window.removeEventListener('storage', onStorage);
    };
  }, []);
  return {
    draft: state,
    patch: patchDraft,
    patchBody,
    reset: resetDraft,
    bump: bumpStepReached,
  };
}

// =====================================================================
// Centralized step validators.  Every guard and progress check reads
// these functions rather than reimplementing the rules.
// =====================================================================

function isValidNumberInRange(v, lo, hi) {
  const n = Number(v);
  return Number.isFinite(n) && n >= lo && n <= hi;
}

export function isDaysValid(d = current) {
  return isValidNumberInRange(d.days, 1, 5)
    && isValidNumberInRange(d.meals_per_day, 1, 5);
}

export function isGoalValid(d = current) {
  return typeof d.health_goal === 'string' && d.health_goal.length > 0;
}

export function isBodyValid(d = current) {
  const b = d.body || {};
  if (!isValidNumberInRange(b.age, 13, 100)) return false;
  if (!b.sex) return false;
  if (b.sex === 'other' || b.sex === 'prefer_not_to_say') {
    if (!b.calculation_basis) return false;
  }
  if (!isValidNumberInRange(b.normalized_height_cm, 120, 230)) return false;
  if (!isValidNumberInRange(b.normalized_weight_kg, 30, 300)) return false;
  if (!b.activity_level) return false;
  return true;
}

export function isCaloriesValid(d = current) {
  const src = d.calorie_source;
  if (src !== 'calculated' && src !== 'custom') return false;
  return isValidNumberInRange(d.calorie_target, 1000, 5000);
}

export function isMacrosValid(d = current) {
  if (!d.macro_preset) return false;
  if (d.macro_preset !== 'custom') return true;
  return ['protein_g', 'carbs_g', 'fat_g'].every((k) =>
    isValidNumberInRange(d[k], 0, 500),
  );
}

export function isAllergiesValid(d = current) {
  if (!Array.isArray(d.allergies) || d.allergies.length === 0) return false;
  if (d.allergies.includes('other') && !d.allergies_other_description.trim()) return false;
  return true;
}

// Restrictions are optional; only fails validation if "Other" chosen w/o text.
export function isRestrictionsValid(d = current) {
  if (!Array.isArray(d.restrictions)) return true;
  if (d.restrictions.includes('other') && !d.restrictions_other_description.trim())
    return false;
  return true;
}

export function isPreferencesValid(d = current) {
  return Array.isArray(d.preferences) && d.preferences.length > 0;
}

// Map wizard step (1..9) → all prerequisite validators that must pass
// before that step is safely accessible.
export const STEP_VALIDATORS = {
  1: [],
  2: [isDaysValid],
  3: [isDaysValid, isGoalValid],
  4: [isDaysValid, isGoalValid, isBodyValid],
  5: [isDaysValid, isGoalValid, isBodyValid, isCaloriesValid],
  6: [isDaysValid, isGoalValid, isBodyValid, isCaloriesValid, isMacrosValid],
  7: [isDaysValid, isGoalValid, isBodyValid, isCaloriesValid, isMacrosValid, isAllergiesValid],
  8: [isDaysValid, isGoalValid, isBodyValid, isCaloriesValid, isMacrosValid, isAllergiesValid, isRestrictionsValid],
  9: [isDaysValid, isGoalValid, isBodyValid, isCaloriesValid, isMacrosValid, isAllergiesValid, isRestrictionsValid, isPreferencesValid],
};

export function canAccessStep(step, d = current) {
  const vs = STEP_VALIDATORS[step] || [];
  return vs.every((fn) => fn(d));
}

// First step whose prerequisites fail. Returns the step the user should
// be sent to. When all prior steps are valid, returns the given step
// (used to redirect a user forward to their in-progress screen).
export function firstIncompleteStep(d = current) {
  const validators = [
    { step: 1, fn: isDaysValid },
    { step: 2, fn: isGoalValid },
    { step: 3, fn: isBodyValid },
    { step: 4, fn: isCaloriesValid },
    { step: 5, fn: isMacrosValid },
    { step: 6, fn: isAllergiesValid },
    { step: 7, fn: isRestrictionsValid },
    { step: 8, fn: isPreferencesValid },
  ];
  for (const { step, fn } of validators) {
    if (!fn(d)) return step;
  }
  return 9;
}

export const STEP_ROUTES = {
  1: '/plan/days',
  2: '/plan/goal',
  3: '/plan/body',
  4: '/plan/calories',
  5: '/plan/macros',
  6: '/plan/allergies',
  7: '/plan/restrictions',
  8: '/plan/preferences',
  9: '/plan/generate',
};

export const STEP_LABELS = {
  1: 'Days & Meals',
  2: 'Main Goal',
  3: 'Body Info',
  4: 'Calories',
  5: 'Macros',
  6: 'Allergies',
  7: 'Restrictions',
  8: 'Preferences',
  9: 'Building',
};
